import './ProductCard.css';
import React, { useState, useEffect } from 'react';
import Breadcrumbs from '../Breadcrumbs/Breadcrumbs';
import store, { addToCart } from '../../store/store';
import Alert from '../Alert/Alert';
import FavouriteButton from '../FavouriteButton/FavouriteButton';
import Footer from '../Footer/Footer';
import { ArrowUp, ArrowDown, X } from 'lucide-react';

import { useSelector } from 'react-redux';

const getSafeDescriptionHtml = (description) => {
  if (!description || !/<[a-z][\s\S]*>/i.test(description) || typeof DOMParser === 'undefined') {
    return null;
  }

  const allowedTags = new Set([
    'P', 'BR', 'STRONG', 'B', 'EM', 'I', 'UL', 'OL', 'LI', 'H2', 'H3', 'H4',
    'BLOCKQUOTE', 'DIV', 'SPAN', 'A', 'BUTTON',
  ]);
  const blockedTags = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'FORM', 'INPUT', 'SELECT', 'TEXTAREA']);
  const documentNode = new DOMParser().parseFromString(description, 'text/html');

  const cleanNode = (node) => {
    Array.from(node.children).forEach(cleanNode);

    if (blockedTags.has(node.tagName)) {
      node.remove();
      return;
    }

    if (!allowedTags.has(node.tagName)) {
      node.replaceWith(...Array.from(node.childNodes));
      return;
    }

    Array.from(node.attributes).forEach((attribute) => node.removeAttribute(attribute.name));

    if (node.tagName === 'BUTTON') {
      node.setAttribute('type', 'button');
    }
  };

  Array.from(documentNode.body.children).forEach(cleanNode);
  return documentNode.body.innerHTML;
};

const ProductCard = ({ product }) => {
  const images = product?.ImagesPath?.$values || [];
  const isForOrder = product?.IsForOrder === true;
  const productKey = `${isForOrder ? 'aproduct' : 'product'}:${product?.ProductId || ''}`;
  const productPrice = Number(product?.Price || 0).toLocaleString('ru-RU');
  const descriptionHtml = getSafeDescriptionHtml(product?.Description);

  //alert
  const [alertVisible, setAlertVisible] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('error');

  const cartIds = useSelector((state) => state.cart.cartIds);

  const isInCart = cartIds.includes(productKey);
  const isDisabled = !isForOrder && !product?.IsInStock;
  const addToCartSelected = isDisabled ? "Не в наличии" : (isInCart ? "Уже в корзине" : "Добавить в корзину");
  const selectedShipping = !isForOrder && product?.IsInStock && product?.ShippingType
    ? "Примерная дата доставки: " + product.ShippingType
    : "";

  const handleAddToCart = async () => {
    if (isInCart) {
      window.location.href = '/cart';
      return
    }
    const data = {
      pid: product.ProductId
    }
    try {
      const resp = await fetch(isForOrder ? '/Cart/AddAProduct' : '/Cart/Add', {
        "method": "POST",
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });
      if (resp.ok) {
        store.dispatch(addToCart(productKey));

      } else {
        const respData = await resp.json();
        setAlertMessage(respData.message);
        setAlertType('error');
        setAlertVisible(true);
        setTimeout(() => {
          setAlertVisible(false);
        }, 3000);
      }
    } catch (err) {
      setAlertMessage('Ошибка добавления в корзину');
      setAlertType('error');
      setAlertVisible(true);

      // Скрываем уведомление через 3 секунды
      setTimeout(() => {
        setAlertVisible(false);
      }, 3000);
    }
  };


  const [mainImage, setMainImage] = useState(images[0]?.URL || "");
  const [isModalOpen, setIsModalOpen] = useState(false); // Состояние модального окна
  const [currentImageIndex, setCurrentImageIndex] = useState(0); // Индекс текущего изображения
  const [isImageZoomed, setIsImageZoomed] = useState(false);
  const lastWheelTime = React.useRef(0);
  const thumbnailRailRef = React.useRef(null);
  const touchStartRef = React.useRef(null);
  const didSwipeRef = React.useRef(false);

  const selectImage = (index) => {
    if (!images[index]) {
      return;
    }

    setMainImage(images[index].URL);
    setCurrentImageIndex(index);
    setIsImageZoomed(false);
  };

  useEffect(() => {
    setMainImage(images[0]?.URL || "");
    setCurrentImageIndex(0);
  }, [product?.ProductId]);

  // Обработчик для открытия изображения в модальном окне
  const handleImageClick = (index) => {
    if (!images[index]) {
      return;
    }
    selectImage(index);
    setIsModalOpen(true);
  };

  // Обработчик для закрытия модального окна
  const closeModal = () => {
    setIsModalOpen(false);
    setIsImageZoomed(false);
  };
  // Обработчик для закрытия модального окна при клике на фон
  const handleBackgroundClick = (e) => {
    if (e.target === e.currentTarget) {  // Проверка, что клик был именно на фоне
      closeModal();
    }
  };
  // Переключение на следующее изображение
  const nextImage = () => {
    if (images.length === 0) {
      return;
    }
    const nextIndex = (currentImageIndex + 1) % images.length;
    selectImage(nextIndex);
  };

  // Переключение на предыдущее изображение
  const prevImage = () => {
    if (images.length === 0) {
      return;
    }
    const prevIndex = (currentImageIndex - 1 + images.length) % images.length;
    selectImage(prevIndex);
  };
  // Добавление обработчика для стрелок на клавиатуре
  useEffect(() => {
    const handleKeydown = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        nextImage();
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        prevImage();
      } else if (e.key === 'Escape') {
        closeModal();
      }
    };

    if (isModalOpen) {
      window.addEventListener('keydown', handleKeydown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeydown);
    };
  }, [isModalOpen, currentImageIndex]);

  useEffect(() => {
    if (!isModalOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    images.forEach((image) => {
      const preload = new Image();
      preload.src = image.URL;
    });
    return () => { document.body.style.overflow = previousOverflow; };
  }, [isModalOpen]);

  const handleModalWheel = (event) => {
    event.preventDefault();
    const now = Date.now();
    if (now - lastWheelTime.current < 420 || Math.abs(event.deltaY) < 8) return;
    lastWheelTime.current = now;
    if (event.deltaY > 0) nextImage();
    else prevImage();
  };

  const handleTouchStart = (event) => {
    const touch = event.touches[0];
    didSwipeRef.current = false;
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchEnd = (event) => {
    const start = touchStartRef.current;
    const touch = event.changedTouches[0];
    touchStartRef.current = null;
    if (!start || !touch || isImageZoomed) return;
    const deltaX = touch.clientX - start.x;
    const deltaY = touch.clientY - start.y;
    if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) < 42) return;
    didSwipeRef.current = true;
    if (Math.abs(deltaY) >= Math.abs(deltaX)) {
      if (deltaY < 0) nextImage();
      else prevImage();
    } else if (deltaX < 0) nextImage();
    else prevImage();
  };

  const toggleImageZoom = () => {
    if (didSwipeRef.current) {
      didSwipeRef.current = false;
      return;
    }
    setIsImageZoomed((zoomed) => !zoomed);
  };

  useEffect(() => {
    const rail = thumbnailRailRef.current;
    const activeThumbnail = rail?.children[currentImageIndex];
    if (!rail || !activeThumbnail) return;
    rail.scrollTo({
      left: activeThumbnail.offsetLeft - (rail.clientWidth - activeThumbnail.clientWidth) / 2,
      top: activeThumbnail.offsetTop - (rail.clientHeight - activeThumbnail.clientHeight) / 2,
      behavior: 'smooth',
    });
  }, [currentImageIndex, isModalOpen]);

  return (
    <>
    <div className="product-page">
      {alertVisible && (
        <Alert
          message={alertMessage}
          type={alertType}
          onClose={() => setAlertVisible(false)} // Закрытие уведомления вручную
        />
      )}
      {product.Tab && <Breadcrumbs category={product.Tab} catalogKind={isForOrder ? 'preorder' : 'stock'} />}
      <div className="product-layout">
        <div className="product-gallery">
          <div className="product-gallery__grid">
            {images.map((image, index) => (
              <button
                key={index}
                type="button"
                className="product-gallery__item"
                onClick={() => handleImageClick(index)}
                aria-label={`Открыть изображение ${index + 1}`}
              >
                <img
                  src={image.URL}
                  alt={`${product.Title}, вид ${index + 1}`}
                  className="product-gallery__image"
                  loading={index < 2 ? 'eager' : 'lazy'}
                  decoding="async"
                />
              </button>
            ))}
          </div>
          {/* Модальное окно для полного изображения */}
          {isModalOpen && (
            <div
              className="product-modal"
              onClick={handleBackgroundClick} // Закрытие при клике на фон
              onWheel={handleModalWheel}
            >
              <div className={`product-modal__content ${isImageZoomed ? 'is-zoomed' : ''}`} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
                {/* Кнопки для переключения изображений */}
                <div className="product-modal__navigation">
                  <button onClick={prevImage} className="product-modal__arrow" aria-label="Предыдущее изображение">
                    <ArrowUp aria-hidden="true" />
                  </button>
                  <button onClick={nextImage} className="product-modal__arrow" aria-label="Следующее изображение">
                    <ArrowDown aria-hidden="true" />
                  </button>
                </div>
                <button
                  onClick={closeModal}
                  className="product-modal__close"
                  aria-label="Закрыть"
                >
                  <X aria-hidden="true" size={22} strokeWidth={1.75} />
                </button>
                <img
                  src={mainImage}
                  alt={product.Title}
                  className="product-modal__image"
                  onClick={toggleImageZoom}
                  draggable="false"
                />
                {images.length > 1 && (
                  <div className="product-modal__thumbnails" ref={thumbnailRailRef} aria-label="Все изображения товара">
                    {images.map((image, index) => (
                      <button
                        type="button"
                        key={image.URL || index}
                        className={`product-modal__thumbnail ${index === currentImageIndex ? 'is-active' : ''}`}
                        onClick={() => selectImage(index)}
                        aria-label={`Показать изображение ${index + 1}`}
                        aria-current={index === currentImageIndex ? 'true' : undefined}
                      >
                        <img src={image.URL} alt="" loading="eager" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="product-info">
          <div className="product-info__heading">
            <div>
              <div className="product-info__brand">{product.BrandName}</div>
              <h1 className="product-info__title">{product.Title}</h1>
            </div>
            {!isForOrder && <FavouriteButton productId={product.ProductId} />}
          </div>
          {product.Condition && <p className="product-condition">{product.Condition}</p>}
          <div className="product-price">{productPrice} ₽</div>
          {product.SizeStr && <p className="product-size">Размер: {product.SizeStr}</p>}
          {!isForOrder && (
            <>
              <div className="product-availability">
                <p>{selectedShipping}</p>
              </div>

              <button className="product-primary-action" disabled={isDisabled} onClick={handleAddToCart}>
                {addToCartSelected}
              </button>
            </>
          )}
          {isForOrder && (
            <div className="product-availability">
              <p>Под заказ</p>
              <button className="product-primary-action" onClick={handleAddToCart}>
                {addToCartSelected}
              </button>
            </div>
          )}

          {(product.Description || product.Composition || product.OriginCountry || product.ManufacturerId || product.RetailPrice > 0) && (
            <div className="product-details">
              {product.Description && (
                <div className="product-details__description">
                  <h2>Описание</h2>
                  {descriptionHtml ? (
                    <div className="product-description" dangerouslySetInnerHTML={{ __html: descriptionHtml }} />
                  ) : (
                    <p className="product-description">{product.Description}</p>
                  )}
                </div>
              )}

              <dl className="product-details__meta">
                {product.Composition && <><dt>Состав</dt><dd>{product.Composition}</dd></>}
                {product.OriginCountry && <><dt>Страна</dt><dd>{product.OriginCountry}</dd></>}
                {product.ManufacturerId && <><dt>Артикул</dt><dd>{product.ManufacturerId}</dd></>}
                {product.RetailPrice > 0 && <><dt>Розничная цена</dt><dd>${product.RetailPrice.toLocaleString('sr')}</dd></>}
              </dl>
            </div>
          )}
        </div>
      </div>

    </div>
    <Footer />
    </>
  );
}
export default ProductCard;
